import {
  Menu as MenuIcon,
  Lock as LockIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import Logo from './common/Logo';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import NotificationsButton from './NotificationsButton';

const DRAWER_WIDTH = 260;

export default function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isOwner, currentWorkspace } = useWorkspace();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Build nav items - Members only visible to workspace owners
  const navItems = useMemo(() => {
    const items = [
      { path: '/vault', label: 'Vault', icon: <LockIcon /> },
      { path: '/sharing', label: 'Sharing', icon: <ShareIcon /> },
    ];

    // Only show Members menu for workspace owners
    if (isOwner) {
      items.push({ path: '/members', label: 'Members', icon: <PeopleIcon /> });
    }

    items.push({ path: '/settings', label: 'Settings', icon: <SettingsIcon /> });

    return items;
  }, [isOwner]);

  const currentNavIndex = navItems.findIndex(
    (item) => location.pathname.startsWith(item.path)
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Logo size="medium" />
      </Toolbar>
      <Divider />
      {/* Workspace Switcher */}
      {currentWorkspace && (
        <>
          <Box sx={{ p: 1 }}>
            <WorkspaceSwitcher />
          </Box>
          <Divider />
        </>
      )}
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname.startsWith(item.path)}
              onClick={() => {
                navigate(item.path);
                setDrawerOpen(false);
              }}
              sx={{ borderRadius: 1, mx: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, mx: 1 }}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      {user && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar - Mobile */}
      {isMobile && (
        <AppBar position="fixed">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Logo size="small" />
            </Box>
            <NotificationsButton />
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Drawer - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pb: isMobile ? 8 : 0,
          pt: isMobile ? 8 : 0,
        }}
      >
        {/* Desktop Header with Notifications */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              px: 3,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <NotificationsButton />
          </Box>
        )}
        <Outlet />
      </Box>

      {/* Bottom Navigation - Mobile */}
      {isMobile && (
        <BottomNavigation
          value={currentNavIndex}
          onChange={(_, newValue) => {
            navigate(navItems[newValue].path);
          }}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
          showLabels
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
}

